/**
 * WebSmMissionLive — the accepted mission, live: their case, open, the 24h
 * window (atmosphere only — nothing happens at zero) running on a screen set
 * into its lid — digits over 24 hour-segments going out, the hour under way
 * blinking — then the mission (title and what to do) and four moves:
 *   - View mission: in through the laptop to its desktop, where the
 *     mission's file opens out into the mission page again.
 *   - Field reports: example debriefs from other operatives.
 *   - Report in: the vault plays — a lock on the operative's vault door
 *     slides open (tap to skip) — then CLICK1 → the debrief.
 *   - Speak to handler: texts their handler; locked until their fourth mission, when
 *     they are given one.
 *
 * Options: Title / Prompt / DoneLabel / BgColor / AccentColor.
 */
import { useHandlerCompanionEnabled } from "./companion-config";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useFunnelStore, useStepperStore } from "@/store";
import {
  useAcceptedMission,
  useMissionFile,
} from "@/features/funnel/api/use-mission-file";
import { useFunnelSession } from "@/features/funnel/api/use-funnel";
import { useDailyDoors } from "@/features/funnel/api/use-doors";
import { smsHref } from "@/features/funnel/lib/sms";
import { ACTION_BTN_CLASS, stepVars } from "./step-shell";
import type { StepTypeProps } from "./types";
import noHandlerDesk from "./no-handler-desk.jpg";
import { VaultRun } from "./vault-run";
import { Laptop } from "./laptop";
import { LaptopDesktop } from "./laptop-desktop";
import { VAULT_CLIPS } from "./vault-clips";
import { ReportWindow } from "./report-window";
import { Counterstrike } from "./counterstrike";
import {
  CASE_LIGHT,
  FRONT_WALL_CLIP,
  OPEN_CASES,
  OPEN_CASE_STYLE,
  screenCentre,
  tierOfLevel,
} from "./open-case";
import { filedCount } from "../dev-mission";
import { LidClock, WINDOW_MS } from "./lid-clock";
import { MissionSteps } from "./steps-file";
import { chainFor, progressKey, stepsFor } from "./mission-steps";
import { missionStepsOn } from "./comeback-config";
import { useMissionProgress } from "@/store/mission-progress";
import { streakFrom } from "../streak";

/** The ring and clock turn red for the last stretch. */
const LATE_MS = 2 * 60 * 60 * 1000;
/** They are given a handler with their fourth mission (three filed). */
const HANDLER_AT = 4;

/** The zoom in through the laptop's screen. */
const REVIEW_ZOOM_MS = 900;

export default function WebSmMissionLiveType({
  getOpt,
  dispatch,
  reportMode,
}: StepTypeProps & { reportMode?: "voice" | "written" }) {
  const liveMissionId = useFunnelStore((s) => s.missionId);
  const companionName = useFunnelStore((s) => s.handler);
  const companionEnabled = useHandlerCompanionEnabled();
  const bg = getOpt("BgColor") || "#07080a";
  const accent = getOpt("AccentColor") || "#d6a94e";
  const title = getOpt("Title") || "MISSION LIVE";
  const prompt = getOpt("Prompt") || "It's yours. Go.";
  const doneLabel = getOpt("DoneLabel") || "Report in";

  const storedTier = useFunnelStore((s) => s.tier);
  const acceptedAt = useFunnelStore((s) => s.acceptedAt);
  const missionQuery = useAcceptedMission();
  const { data: mission } = missionQuery;
  // No mission to show and none coming: it failed to load, or no id can be
  // derived (no tier, or the day's doors failed). Without this the laptop and
  // buttons would sit disabled for good.
  const doors = useDailyDoors();
  const missionStuck =
    !mission &&
    (missionQuery.isError ||
      (missionQuery.fetchStatus === "idle" && !doors.isPending));
  const retryMission = () =>
    // (a disabled query has no id to fetch: the doors derive it)
    void (missionQuery.isError ? missionQuery.refetch() : doors.refetch());
  // (opened from a texted link on another device: the tier off the mission)
  const tier = storedTier ?? tierOfLevel(mission?.level);
  const { data: session } = useFunnelSession();
  // This mission opens the next lock: one per mission already in their file
  // (the debrief for this one is not filed yet), the seventh from then on.
  // unread (loading, or failed) reads as "none filed": Report in waits for
  // it, or the vault would open lock 1 whatever mission this is
  const {
    data: filed = [],
    isSuccess: fileLoaded,
    isError: fileFailed,
    refetch: refetchFile,
  } = useMissionFile();
  const filedN = filedCount(filed);
  // the Comeback Loop: the first three missions as small steps on the
  // laptop, the full briefing a page behind them. From the fourth on (the
  // handler's missions, missionId 0) the screen is as it was.
  const steps = missionStepsOn(filedN) && (liveMissionId ?? 0) > 0;
  // The lock is fixed when they submit: filing reloads their file, which
  // then counts this mission too, and the vault opened the NEXT lock (lock 2
  // on a first mission). It also keeps the clip primed in the tap the same.
  const [lockAt, setLockAt] = useState<number | null>(null);
  const setFiledMissionNo = useFunnelStore((s) => s.setFiledMissionNo);
  const lock = Math.min(lockAt ?? filedN, VAULT_CLIPS.length - 1);
  const vaultClip = VAULT_CLIPS[lock];
  const missionNo = filedN + 1;
  // Already reported this one (e.g. back from the mission-accepted text's
  // link after filing it): say so instead of running the clock.
  const filedThis = mission
    ? filed.find(
        (f) =>
          f.name.trim().toUpperCase() === mission.name.trim().toUpperCase(),
      )
    : undefined;
  const goTo = useStepperStore((s) => s.goTo);
  const handlerNumber =
    missionNo >= HANDLER_AT ? session?.handlerNumber : undefined;

  // The window ticks once a second; atmosphere, so it simply rests at zero.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = acceptedAt ? acceptedAt + WINDOW_MS - now : WINDOW_MS;
  const late = left < LATE_MS;

  // In through the laptop's screen to its desktop: to view the mission, or
  // to report in on it
  const [review, setReview] = useState<"zoom" | "on" | "out" | null>(
    reportMode ? "on" : null,
  );
  const [onScreen, setOnScreen] = useState<"view" | "report" | "brief">(
    reportMode ? "report" : "view",
  );
  const openLaptop = (what: "view" | "report") => {
    if (review || !mission) return;
    (document.activeElement as HTMLElement | null)?.blur();
    setOnScreen(what);
    setReview("zoom");
  };
  const reviewIt = () =>
    hasChain && stepsLeft ? dispatch("STEPS") : openLaptop("view");
  // reporting in on the laptop: once they submit, this stays the live screen
  // (not the reported-already one) through the vault run
  const [filing, setFiling] = useState(false);
  // closed with steps half done: a promise, so leaving feels unfinished
  const [warm, setWarm] = useState("");
  const closeLaptop = () => {
    if (reportMode) {
      dispatch("CLOSE");
      return;
    }
    // back out through the screen to the case
    (document.activeElement as HTMLElement | null)?.blur();
    setReview("out");
    if (steps && stepsDone > 0 && stepsDone < stepsTotal && nextStep) {
      setWarm(`Step ${stepsDone + 1} is waiting. I'll keep it warm.`);
      window.setTimeout(() => setWarm(""), 4500);
    }
  };

  useEffect(() => {
    if (review !== "zoom" && review !== "out") return;
    const timer = window.setTimeout(
      () => setReview(review === "zoom" ? "on" : null),
      REVIEW_ZOOM_MS,
    );
    return () => window.clearTimeout(timer);
  }, [review]);

  // Report in: the vault, then the debrief (once).
  const [vault, setVault] = useState(false);
  const vaultRef = useRef<HTMLVideoElement>(null);
  // set once VaultRun plays the clip on arrival
  const vaultPlaying = useRef(false);
  const done = useRef(false);
  const finish = () => {
    if (done.current) return;
    done.current = true;
    vaultRef.current?.pause();
    dispatch(reportMode === "written" ? "FILED" : "CLICK1");
  };
  // Started inside a tap so the browser lets it play with sound (blocked
  // with sound, muted instead), then held on its first frame while the
  // camera pushes down the corridor (VaultRun plays it on arrival).
  const primeVault = () => {
    const v = vaultRef.current;
    if (!v) return;
    // a clip slow to buffer settles this play() late: by then VaultRun may
    // have started it, and pausing would freeze the door on its first frame
    const hold = () => {
      if (!vaultPlaying.current) v.pause();
    };
    v.play()
      .catch(() => {
        v.muted = true;
        return v.play();
      })
      .then(hold, hold);
  };
  const runVault = () => {
    if (
      !vaultRef.current ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      finish();
      return;
    }
    setVault(true);
  };
  // Report in: on the laptop (the report is filed there)
  const hasVoiceBranch = useStepperStore((state) =>
    state.graph?.steps[state.currentStepName]?.branches.some(
      (branch) => branch.eventV === "VOICE",
    ),
  );
  const reportIn = () =>
    hasVoiceBranch ? dispatch("VOICE") : openLaptop("report");
  // every step stamped: on to the report, on the laptop already open
  const stamp = () =>
    hasVoiceBranch ? dispatch("VOICE") : setOnScreen("report");
  const stepsDoneList = useMissionProgress((s) =>
    mission ? s.done[progressKey(mission.missionId)] : undefined,
  );
  const stepsDone = stepsDoneList?.length ?? 0;
  const graph = useStepperStore((s) => s.graph);
  // the adventure's own chain for this mission, when it has one: Start goes
  // down the STEPS branch into it instead of opening the laptop
  const chain = mission ? chainFor(graph, mission.missionId) : [];
  const hasChain =
    chain.length > 0 &&
    !!useStepperStore
      .getState()
      .graph?.steps[useStepperStore.getState().currentStepName]?.branches.some(
        (b) => b.eventV === "STEPS",
      );
  const stepsTotal = mission ? chain.length || stepsFor(mission).length : 0;
  // days in a row, from their file
  const streak = streakFrom(
    filed
      .map((f) => (f.filedAt ? new Date(f.filedAt).getTime() : NaN))
      .filter((t) => !Number.isNaN(t)),
  );
  // steps still open: the steps are the one move, and Report in waits
  const stepsLeft = steps && !!mission && stepsDone < stepsTotal;
  // the next step, named under the button: the next move is never a mystery
  const nextStep = mission
    ? (chain.length ? chain.map((c) => c.text) : stepsFor(mission)).find(
        (_, i) => !stepsDoneList?.includes(i),
      )
    : undefined;

  // Already reported in on this one (e.g. back on the mission-accepted
  // text's link after filing it, or a browser still holding a filed mission):
  // nothing to do here, so straight on to HQ. Never a screen of its own.
  // (only when there is an HQ to go to: otherwise the page stays, not blank)
  const hasHq = useStepperStore((s) => !!s.graph?.steps["Sign off"]);
  const backToHq = () => goTo(hasHq ? "Sign off" : "Doors");
  const alreadyFiled = !!filedThis && !filing && hasHq;
  useEffect(() => {
    if (alreadyFiled) goTo("Sign off");
  }, [alreadyFiled, goTo]);
  if (alreadyFiled) return null;

  return (
    <section
      style={
        {
          ...stepVars({ accent, bg }),
          // the case's own light; red for the last stretch
          "--sm-ring": late ? "#e0342a" : CASE_LIGHT[tier ?? "beginner"],
        } as CSSProperties
      }
      className="relative h-[100dvh] overflow-clip bg-[var(--sm-bg)] font-sans text-[#eadfc6] [container-type:inline-size]"
    >
      {/* the desk the case was opened on */}
      <img
        src={noHandlerDesk}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />
      <div className="absolute inset-0 bg-black/55" />

      {/* status line */}
      <div className="relative mx-auto flex w-full max-w-[460px] items-center justify-between px-4 pt-[max(12px,2dvh)] font-mono text-[10.5px] tracking-[.24em] uppercase">
        <span className="flex items-center gap-2 text-[var(--sm-ring)]">
          <span className="size-2 animate-pulse rounded-full bg-[#e0342a] shadow-[0_0_10px_#e0342a]" />
          {title}
        </span>
        <span className="text-[color-mix(in_srgb,var(--sm-ring)_55%,#8c8070)]">
          {/* the number only once their file says which this is */}
          {fileLoaded ? `Mission ${missionNo}` : "Mission"}
        </span>
      </div>

      {/* the mission's name, over its case */}
      <div className="absolute inset-x-0 top-[7.5%] px-4 text-center font-stencil text-[clamp(22px,7cqw,32px)] leading-[1.1] tracking-[.04em] text-[var(--sm-ring)] uppercase [text-shadow:0_0_14px_color-mix(in_srgb,var(--sm-ring)_50%,transparent)]">
        {mission?.name || prompt}
      </div>

      {/* their case, open, the clock running on a screen set into its lid */}
      <div
        inert={!!review || vault}
        style={{
          ...OPEN_CASE_STYLE,
          transformOrigin: screenCentre(tier ?? "beginner"),
          animation:
            review === "zoom"
              ? `smIntoScreen ${REVIEW_ZOOM_MS}ms cubic-bezier(.6,0,.9,.5) both`
              : review === "out"
                ? `smIntoScreen ${REVIEW_ZOOM_MS}ms cubic-bezier(.1,.5,.4,1) reverse both`
                : undefined,
        }}
        className="absolute [container-type:inline-size]"
      >
        <img
          src={OPEN_CASES[tier ?? "beginner"]}
          alt=""
          className="absolute inset-0 size-full"
        />
        {/* in the case, as when it was opened: the laptop; tap it to read
            the mission file */}
        <Laptop
          tier={tier ?? "beginner"}
          onClick={mission ? reviewIt : undefined}
        />
        <LidClock tier={tier ?? "beginner"} left={left} />
        {/* the front wall, over them: they sit down inside the case */}
        <img
          src={OPEN_CASES[tier ?? "beginner"]}
          alt=""
          style={{ clipPath: FRONT_WALL_CLIP }}
          className="pointer-events-none absolute inset-0 z-[2] size-full"
        />
        {/* the steps first: Report in only once every step is done */}
        <div className="absolute top-[102%] left-1/2 z-[3] w-[min(78%,320px)] -translate-x-1/2">
          <button
            type="button"
            onClick={reviewIt}
            disabled={!mission}
            className={
              stepsLeft
                ? `w-full ${ACTION_BTN_CLASS} rounded-[10px] px-6 py-3.5 text-[17px] font-bold tracking-[.3px] shadow-[0_10px_24px_rgba(0,0,0,.55)]`
                : "w-full cursor-pointer rounded-[10px] border border-[color-mix(in_srgb,var(--sm-ring)_55%,transparent)] bg-black/55 px-6 py-3 text-[15.5px] font-semibold text-[#eadfc6] shadow-[0_10px_24px_rgba(0,0,0,.45)] disabled:opacity-50"
            }
          >
            {steps && mission
              ? stepsDone >= stepsTotal
                ? "Every step done"
                : stepsDone === 0
                  ? `Start the mission · ${stepsTotal} steps`
                  : `Continue · ${stepsDone} of ${stepsTotal} done`
              : "View mission"}
          </button>
          {stepsLeft && nextStep ? (
            <div className="mt-1.5 truncate text-center text-[12.5px] text-[#e6dcc4]/80">
              Next: {nextStep}
            </div>
          ) : null}
          {!stepsLeft ? (
            <button
              type="button"
              onClick={reportIn}
              disabled={!mission || !fileLoaded}
              className={`mt-2.5 w-full ${ACTION_BTN_CLASS} rounded-[10px] px-6 py-3.5 text-[17px] font-bold tracking-[.3px] shadow-[0_10px_24px_rgba(0,0,0,.55)]`}
            >
              {doneLabel}
            </button>
          ) : null}
          {missionStuck ? (
            <div className="mt-2 flex items-center justify-center gap-3 text-[12.5px] text-[#e6dcc4]/80">
              Couldn't load your mission.
              <button
                type="button"
                onClick={retryMission}
                className="cursor-pointer underline underline-offset-2"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={backToHq}
                className="cursor-pointer underline underline-offset-2"
              >
                {hasHq ? "Back to HQ" : "Back to the doors"}
              </button>
            </div>
          ) : fileFailed ? (
            <div className="mt-2 text-center text-[12.5px] text-[#e6dcc4]/80">
              Couldn't load your file —{" "}
              <button
                type="button"
                onClick={() => void refetchFile()}
                className="cursor-pointer underline underline-offset-2"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="mt-2 text-center text-[12.5px] text-[#e6dcc4]/70">
              {warm ? (
                <span className="font-semibold text-[#f2dba6]">{warm}</span>
              ) : stepsLeft ? null : (
                "Done? Tell us how it went."
              )}
            </div>
          )}
        </div>
      </div>

      {/* the handler: given with the fourth mission */}
      <div
        inert={!!review || vault}
        className="absolute inset-x-0 bottom-[max(70px,9dvh)] text-center text-[13px] text-[#a99d86]"
      >
        {!companionEnabled && handlerNumber ? (
          <a
            href={smsHref(handlerNumber, "")}
            className="underline decoration-[#a99d86]/40 underline-offset-2"
          >
            Speak to your handler
          </a>
        ) : null}
      </div>

      {/* reviewing: the screen's black filling the view, then its desktop */}
      {review === "zoom" || review === "out" ? (
        <div
          style={{
            animation:
              review === "zoom"
                ? `smFadeIn ${REVIEW_ZOOM_MS * 0.4}ms ease-in ${REVIEW_ZOOM_MS * 0.35}ms both`
                : `smFadeIn ${REVIEW_ZOOM_MS * 0.4}ms ease-out reverse both`,
          }}
          className="absolute inset-0 z-[29] bg-black"
        />
      ) : null}
      {review === "on" && mission ? (
        <LaptopDesktop left={left}>
          {onScreen === "report" ? (
            <ReportWindow
              mode={reportMode ?? "written"}
              onBranch={dispatch}
              tier={tier ?? "beginner"}
              mission={mission}
              lockNo={filedN + 1}
              onSubmitTap={() => {
                setLockAt(filedN);
                // the debrief counts this mission off the same lock
                setFiledMissionNo(filedN + 1);
                setFiling(true);
                primeVault();
              }}
              onFiled={() => {
                // filed: its steps are done with (a new mission starts
                // clean), then out of the laptop, down to the vault, then
                // the agent's well done
                useMissionProgress
                  .getState()
                  .clear(progressKey(mission.missionId));
                setReview(null);
                runVault();
              }}
              onClose={closeLaptop}
            />
          ) : steps && onScreen === "view" ? (
            <MissionSteps
              tier={tier ?? "beginner"}
              mission={mission}
              handler={companionName}
              missionNo={fileLoaded ? missionNo : undefined}
              streak={streak}
              onStamp={stamp}
              onClose={closeLaptop}
            />
          ) : (
            <Counterstrike
              review
              tier={tier ?? "beginner"}
              mission={mission}
              acceptLabel={steps ? "Back to the steps" : "Done"}
              onAccept={steps ? () => setOnScreen("view") : closeLaptop}
            />
          )}
        </LaptopDesktop>
      ) : null}

      {/* reporting in: down the corridor to the vault, the lock opens and is
          honoured (rendered from the start, hidden, so the tap can start it) */}
      <VaultRun
        src={vaultClip}
        lock={lock}
        active={vault}
        videoRef={vaultRef}
        playingRef={vaultPlaying}
        onDone={finish}
      />

      <style>{`
        @keyframes smRise{from{opacity:0;transform:translateY(60%)}to{opacity:1;transform:none}}
        @keyframes smIntoScreen{from{scale:1}to{scale:6}}
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smSectionIn{from{opacity:0;transform:translateY(8cqw)}to{opacity:1;transform:none}}
        @keyframes smBlink{0%{opacity:1}50%{opacity:.15}100%{opacity:1}}
        @keyframes smScreenOn{0%{transform:scaleY(.02);filter:brightness(3)}30%{transform:scaleY(1);filter:brightness(1.8)}45%{opacity:.6}55%{opacity:1;filter:none}70%{opacity:.85}100%{opacity:1;transform:none;filter:none}}
      `}</style>
    </section>
  );
}
